export type NotificationItem = {
  id: string;
  title: string;
  message: string;
  source_type: string;
  target: string | null;
  metadata: Record<string, unknown>;
  is_read: boolean;
  read_at: string | null;
  created_at: string | null;
  relative_time: string | null;
};

type NotificationPreviewResponse = {
  count: number;
  items: NotificationItem[];
};

type NotificationPageResponse = {
  data: {
    data: NotificationItem[];
    current_page: number;
    last_page: number;
    total: number;
  };
  summary: {
    active_filter: 'all' | 'unread';
    total_count: number;
    unread_count: number;
  };
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
const notificationEventName = 'cares:notifications-updated';

const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(`Notification request failed (${response.status}).`);
  }

  return response.json() as Promise<T>;
};

export const fetchNotificationPreview = async (): Promise<NotificationPreviewResponse> =>
  readJson<NotificationPreviewResponse>(
    await fetch('/api/v1/secure/notifications/unread-count', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

export const fetchNotificationsPage = async (
  page = 1,
  filter: 'all' | 'unread' = 'all'
): Promise<NotificationPageResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: '10',
    filter,
  });

  return readJson<NotificationPageResponse>(
    await fetch(`/api/v1/secure/notifications?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );
};

export const updateNotificationReadState = async (
  notificationId: string,
  isRead: boolean
): Promise<NotificationItem> => {
  const response = await fetch(`/api/v1/secure/notifications/${notificationId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      ...jsonHeaders,
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({
      is_read: isRead,
    }),
  });

  const payload = await readJson<{ notification: NotificationItem }>(response);

  emitNotificationsUpdated();

  return payload.notification;
};

export const markAllNotificationsRead = async (): Promise<number> => {
  const response = await fetch('/api/v1/secure/notifications/mark-all-read', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      ...jsonHeaders,
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({}),
  });

  const payload = await readJson<{ updated: number }>(response);

  emitNotificationsUpdated();

  return payload.updated;
};

export const deleteNotification = async (notificationId: string): Promise<void> => {
  await readJson<{ deleted: boolean }>(
    await fetch(`/api/v1/secure/notifications/${notificationId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        ...jsonHeaders,
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );

  emitNotificationsUpdated();
};

export const emitNotificationsUpdated = () => {
  window.dispatchEvent(new CustomEvent(notificationEventName));
};

export const subscribeToNotificationsUpdated = (listener: () => void) => {
  const handler = () => listener();

  window.addEventListener(notificationEventName, handler);

  return () => window.removeEventListener(notificationEventName, handler);
};
